import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select';
import { TradeSide } from '@/components/common/SidePanels/TradeSidePanel/schema';
import { useTranslation } from 'react-i18next';
import { InfoWithIcon } from '@/components/common/SidePanels/TransferSidePanel/parts/InfoWithIcon';
import {
  NumberNoNaN,
  thousandFormatNumbroCurrency,
} from '@fintronners/react-utils/src/numberUtilsTSX';
import CurrencyInput from 'react-currency-input-field';
import { Button } from '@/components/common/Button';
import { BaseFormProps } from '../FixedIncomeTradeForm';
import { Controller, useFormContext } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { WebBodyText14, WebCaption12 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import FixedIncomeTradeBreakdown from '../breakdown/FixedIncomeTradeBreakdown';
import {
  FixedIncomeTradeFormKeys,
  FixedIncomeTradeFormValues,
} from '@/components/common/SidePanels/TradeSidePanel/FixedIncome/schema';
import cn from '@/utils/tailwind';

type FixedIncomeTradeSellFormProps = BaseFormProps;

export const FixedIncomeTradeSellForm = ({ breakdown }: FixedIncomeTradeSellFormProps) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<FixedIncomeTradeFormValues>();

  const amount = useWatch({
    control,
    name: FixedIncomeTradeFormKeys.AMOUNT,
  });

  const limitPrice = useWatch({
    control,
    name: FixedIncomeTradeFormKeys.LIMIT_PRICE,
  });

  const handleSellAll = () => {
    setValue(FixedIncomeTradeFormKeys.AMOUNT, breakdown.tradableUnits);
  };

  return (
    <>
      <div>
        <Controller
          control={control}
          name={FixedIncomeTradeFormKeys.SIDE}
          render={({ field: { value, onChange } }) => (
            <Select value={value} defaultValue={value} onValueChange={onChange} disabled>
              <SelectTrigger className="my-7">
                <SelectValue placeholder={t('common.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={TradeSide.SELL}>
                    {t('miscScreens.tradeOptions.tradeLimitSellOrderType')}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
        <div className="flex flex-col gap-1">
          <WebBodyText14.Regular className="text-grey72">
            {t('miscScreens.tradeOptions.tradeLimitBuyOrderLimit')}
          </WebBodyText14.Regular>
          <WebCaption12.Regular className="text-grey72">
            {t('miscScreens.tradeOptions.fixedIncome.limitDesc')}
          </WebCaption12.Regular>
          <Controller
            control={control}
            name={FixedIncomeTradeFormKeys.LIMIT_PRICE}
            render={({ field: { value, onChange } }) => (
              <CurrencyInput
                className="mt-2 w-full border rounded h-[35px] px-3 py-2 text-sm text-darkestBlue focus-visible:outline-primary"
                value={value}
                placeholder="0.00"
                decimalsLimit={2}
                intlConfig={{ locale: 'en-US' }}
                onValueChange={(_value) => onChange(NumberNoNaN(_value))}
              />
            )}
          />

          <InfoWithIcon
            message={t('miscScreens.tradeOptions.fixedIncome.sellLimitPriceWarning', {
              limitPrice: thousandFormatNumbroCurrency(limitPrice),
            })}
          />
        </div>
        <div className="my-7">
          <WebBodyText14.Regular className="text-grey72">
            {t('miscScreens.tradeOptions.fixedIncome.bondsQuantity')}
          </WebBodyText14.Regular>
          <Controller
            control={control}
            name={FixedIncomeTradeFormKeys.AMOUNT}
            render={({ field: { value, onChange } }) => (
              <CurrencyInput
                className="w-full text-[60px] text-primary outline-none disabled:text-grey25 bg-transparent"
                value={value || ''}
                intlConfig={{ locale: 'en-US' }}
                onValueChange={(_value) => onChange(NumberNoNaN(_value))}
                allowNegativeValue={false}
                allowDecimals={false}
                placeholder="0"
              />
            )}
          />

          {errors[FixedIncomeTradeFormKeys.AMOUNT]?.message && amount > 0 && (
            <WebCaption12.Regular className="text-red leading-none">
              {errors[FixedIncomeTradeFormKeys.AMOUNT]?.message}
            </WebCaption12.Regular>
          )}
          <WebBodyText14.Regular
            className={cn('text-primary', {
              'mt-2': Boolean(errors[FixedIncomeTradeFormKeys.AMOUNT]?.message),
            })}
          >
            {t('miscScreens.tradeOptions.fixedIncome.currentFullHoldings', {
              amount: breakdown.tradableUnits,
            })}
          </WebBodyText14.Regular>
          <Button variant="secondary" type="button" className="mt-2" onClick={handleSellAll}>
            {t('miscScreens.tradeOptions.fixedIncome.sellAll')}
          </Button>
        </div>

        <FixedIncomeTradeBreakdown breakdown={breakdown} />
      </div>
    </>
  );
};
